import React, { useEffect } from 'react'
import {
	Settings as SettingsIcon,
	Loader2,
	AlertCircle,
	RefreshCw,
	Package,
	Calendar,
	CheckCircle,
} from 'lucide-react'
import { Button, Alert } from '@components/common'
import { useCompanySettingsStore } from '@store/useCompanySettingsStore'
import { ProfileSettings, OrganizationDocuments } from './components'
import { formatDate } from '@utils'
import './CompanySettings.scss'

/**
 * Company Settings Page
 *
 * Self-service settings management for Company Admin
 * - Company profile editing
 */
const CompanySettings = () => {
	// Store
	const {
		company,
		loading,
		saving,
		error,
		fetchCompany,
		updateCompany,
		clearError,
	} = useCompanySettingsStore()

	// Fetch company data on mount
	useEffect(() => {
		fetchCompany()
	}, [fetchCompany])

	/**
	 * Handle save profile
	 */
	const handleSave = async (data) => {
		await updateCompany(data)
	}

	/**
	 * Handle retry on error
	 */
	const handleRetry = () => {
		clearError()
		fetchCompany()
	}

	/**
	 * Render subscription info card
	 */
	const renderSubscriptionInfo = () => {
		if (!company?.package) return null

		const daysRemaining = company.subscription_end
			? Math.ceil((new Date(company.subscription_end) - new Date()) / (1000 * 60 * 60 * 24))
			: null

		return (
			<div className="company-settings__subscription-card">
				<div className="company-settings__subscription-header">
					<Package size={20} />
					<span>Current Plan</span>
				</div>
				<div className="company-settings__subscription-content">
					<div className="company-settings__package-name">
						{company.package.title}
					</div>
					<div className="company-settings__package-price">
						${parseFloat(company.package.price).toFixed(2)}/month
					</div>
				</div>
				{company.subscription_end && (
					<div className="company-settings__subscription-dates">
						<div className="company-settings__date-item">
							<Calendar size={14} />
							<span>Valid until: {formatDate(company.subscription_end)}</span>
						</div>
						{daysRemaining !== null && (
							<div className={`company-settings__days-remaining ${daysRemaining < 30 ? 'warning' : ''}`}>
								{daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Expired'}
							</div>
						)}
					</div>
				)}
				<div className="company-settings__subscription-status">
					<CheckCircle size={14} />
					<span className={`status-${company.status}`}>
						{company.status?.charAt(0).toUpperCase() + company.status?.slice(1)}
					</span>
				</div>
			</div>
		)
	}

	// Loading state
	if (loading && !company) {
		return (
			<div className="company-settings">
				<div className="company-settings__loading">
					<Loader2 size={48} className="company-settings__spinner" />
					<p>Loading company settings...</p>
				</div>
			</div>
		)
	}

	// Error state
	if (error && !company) {
		return (
			<div className="company-settings">
				<div className="company-settings__error">
					<AlertCircle size={48} />
					<h3>Failed to load settings</h3>
					<p>{error}</p>
					<Button variant="primary" onClick={handleRetry}>
						<RefreshCw size={16} />
						Try Again
					</Button>
				</div>
			</div>
		)
	}

	return (
		<div className="company-settings">
			{/* Page Header */}
			<div className="company-settings__header">
				<div className="company-settings__header-content">
					<h1 className="company-settings__title">
						<SettingsIcon size={24} />
						Company Settings
					</h1>
					<p className="company-settings__subtitle">
						Manage your company profile and settings
					</p>
				</div>
			</div>

			{/* Error alert */}
			{error && company && (
				<Alert
					type="error"
					message={error}
					onClose={clearError}
					className="company-settings__alert"
				/>
			)}

			{/* Main Content */}
			<div className="company-settings__content">
				{/* Main Panel */}
				<main className="company-settings__main">
					<div className="company-settings__panel">
						<ProfileSettings
							company={company}
							onSave={handleSave}
							saving={saving}
						/>

						<div className="company-settings__panel-divider" />

						<OrganizationDocuments />
					</div>

					{/* Subscription Info - Right side after profile */}
					{renderSubscriptionInfo()}
				</main>
			</div>
		</div>
	)
}

export default CompanySettings
